<template>
  <v-dialog
    v-model="opened"
    @keydown.esc="$emit('update:opened', false)"
    scrollable
    width="700"
    persistent
  >
    <v-card>
      <v-card-title>
        <span
          class="title text-xs-left"
          v-if="selectedQuestionnairesThatCanBeReminded.length === 1"
          >Send Reminder for
          {{ selectedQuestionnairesThatCanBeReminded[0].formatted_name }}</span
        >
        <span class="title text-xs-left" v-else
          >Send Questionnaire Reminders ({{
            selectedQuestionnairesThatCanBeReminded.length
          }})</span
        >
        <v-spacer />
      </v-card-title>
      <v-card-text class="text-xs-left">
        <v-alert type="info" :value="true" outline v-if="remindersAlreadySent"
          >Are you sure you want to send a reminder? The last reminder was sent
          {{ reminderSentDates }}.</v-alert
        >
        <v-switch
          label="Customize Messages"
          class="pa-0 ma-0"
          v-model="customizeReminderMessages"
        />
        <v-layout column v-if="customizeReminderMessages">
          <span class="font-weight-bold mb-2"
            >Email
            <v-tooltip bottom>
              <v-icon size="15" class="ml-1" slot="activator"> info </v-icon>
              <span
                >This will be sent with a button that will take the client to
                the questionnaire at the bottom: "Open Questionnaire"</span
              >
            </v-tooltip>
          </span>
          <span>Subject</span>
          <v-text-field
            v-model="reminderEmailSubject"
            class="default-input"
            :placeholder="reminderEmailDefaultSubject"
            solo
          />
          <span>Body</span>
          <v-textarea
            v-model="reminderEmailMessage"
            class="default-input"
            :placeholder="reminderEmailDefaultMessage"
            solo
            rows="10"
          />
          <span class="font-weight-bold"
            >In-app Message
            <v-tooltip bottom>
              <v-icon size="15" class="ml-1" slot="activator"> info </v-icon>
              <span
                >This will be sent with a link for the questionnaire at the
                bottom: "Click <u>here</u> to view it."</span
              >
            </v-tooltip>
          </span>
          <v-textarea
            v-model="reminderInAppMessage"
            class="default-input"
            :placeholder="reminderInAppDefaultMessage"
            solo
          />
          <span class="font-weight-bold"
            >SMS Notification
            <v-tooltip bottom>
              <v-icon size="15" class="ml-1" slot="activator"> info </v-icon>
              <span
                >The message will be sent if the client has a phone number and
                has opted in to receive SMS notification.</span
              >
            </v-tooltip>
          </span>
          <v-textarea
            v-model="reminderSmsMessage"
            class="default-input"
            :placeholder="reminderSmsDefaultMessage"
            solo
          />
        </v-layout>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          class="primary text-capitalize"
          @click="sendQuestionnaireReminders()"
        >
          Send Now
        </v-btn>
        <v-btn
          v-if="customizeReminderMessages"
          class="primary--text text-capitalize"
          outline
          @click="saveQuestionnaires()"
        >
          Save & do not Send
        </v-btn>
        <v-btn
          class="error--text text-capitalize"
          outline
          @click="$emit('update:opened', false)"
        >
          cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import API from '@/api';

export default {
  props: {
    opened: {
      type: Boolean,
    },

    selectedQuestionnaires: {
      type: Array,
      default: [],
    },
  },

  data() {
    return {
      customizeReminderMessages: false,
      reminderEmailSubject: '',
      reminderEmailMessage: '',
      reminderInAppMessage: '',
      reminderSmsMessage: '',
      reminderEmailDefaultSubject:
        'Reminder to complete {questionnaire_year} {questionnaire_name}{for_wording}',
      reminderEmailDefaultMessage:
        'Hi {client_fname}!\n\nThis is a reminder to finish the {questionnaire_year} {questionnaire_name}{for_wording}. You still have {number_of_items_left} questions that need to be answered. Please click the button below to begin.\n\nIf you have any problems with the website, please email us at hello@soraban.com\n\nFor all other questions, please send a message using the message icon on the top right.',
      reminderInAppDefaultMessage:
        'This is a reminder to complete the {questionnaire_year} {questionnaire_name}{for_wording}{by_due_date}.',
      reminderSmsDefaultMessage:
        '{firm_name}: Hi {client_fname}, this is a reminder to complete the {questionnaire_year} {questionnaire_name}{for_wording}{by_due_date}.',
    };
  },

  computed: {
    selectedQuestionnairesThatCanBeReminded() {
      return this.selectedQuestionnaires.filter(q => !q.completed && !q.locked);
    },
    remindersAlreadySent() {
      return this.selectedQuestionnairesThatCanBeReminded.some(
        d => !isEmpty(d.last_followed_up),
      );
    },
    reminderSentDates() {
      return this.selectedQuestionnairesThatCanBeReminded
        .filter(d => !isEmpty(d.last_followed_up))
        .map(d => moment(d.last_followed_up).format('ll'))
        .filter(this.onlyUnique)
        .join(', ');
    },
  },

  methods: {
    isEmpty,
    moment,
    onlyUnique(value, index, self) {
      return self.indexOf(value) === index;
    },
    reset() {
      const uniqueEmailSubjects = this.selectedQuestionnaires
        .map(q => q.reminder_email_subject)
        .filter(this.onlyUnique);
      const uniqueEmailMessages = this.selectedQuestionnaires
        .map(q => q.reminder_email_message)
        .filter(this.onlyUnique);
      const uniqueInAppMessages = this.selectedQuestionnaires
        .map(q => q.reminder_in_app_message)
        .filter(this.onlyUnique);
      const uniqueSmsMessages = this.selectedQuestionnaires
        .map(q => q.reminder_sms_message)
        .filter(this.onlyUnique);

      this.reminderEmailSubject =
        uniqueEmailSubjects.length > 1
          ? this.reminderEmailDefaultSubject
          : uniqueEmailSubjects[0];
      this.reminderEmailMessage =
        uniqueEmailMessages.length > 1
          ? this.reminderEmailDefaultMessage
          : uniqueEmailMessages[0];
      this.reminderInAppMessage =
        uniqueInAppMessages.length > 1
          ? this.reminderInAppDefaultMessage
          : uniqueInAppMessages[0];
      this.reminderSmsMessage =
        uniqueSmsMessages.length > 1
          ? this.reminderSmsDefaultMessage
          : uniqueSmsMessages[0];
    },
    async sendQuestionnaireReminders() {
      try {
        const response = await API.questionnaire.sendQuestionnaireReminders({
          questionnaireIds: this.selectedQuestionnaires
            .filter(q => !q.locked)
            .map(q => q.id),
          reminderEmailSubject: this.reminderEmailSubject,
          reminderEmailMessage: this.reminderEmailMessage,
          reminderInAppMessage: this.reminderInAppMessage,
          reminderSmsMessage: this.reminderSmsMessage,
          customize: this.customizeReminderMessages,
        });
        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message:
              'You have sent the reminders. Any necessary emails should go out over the next few minutes.',
          });
          this.$emit('update:opened', false);
          this.$emit('refresh-questionnaires');
        }
      } catch (e) {
        console.log(e);
      }
    },
    async saveQuestionnaires() {
      try {
        const response = await API.questionnaire.saveQuestionnaires({
          questionnaireIds: this.selectedQuestionnairesThatCanBeReminded.map(
            q => q.id,
          ),
          reminderEmailSubject: this.reminderEmailSubject,
          reminderEmailMessage: this.reminderEmailMessage,
          reminderInAppMessage: this.reminderInAppMessage,
          reminderSmsMessage: this.reminderSmsMessage,
        });
        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'You have saved the questionnaires.',
          });
          this.$emit('update:opened', false);
          this.$emit('refresh-questionnaires');
        }
      } catch (e) {
        console.log(e);
      }
    },
  },
  watch: {
    opened(newVal) {
      if (newVal) {
        this.reset();
      }
    },
  },
};
</script>
